import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type Tone = "neutral" | "accent" | "ok" | "warn" | "bad" | "info" | "violet" | "outline" | "brand";

const TONES: Record<Tone, string> = {
  neutral: "bg-surface-2 text-ink-2",
  accent: "bg-accent-soft text-accent-ink",
  ok: "bg-ok-soft text-ok",
  warn: "bg-warn-soft text-warn",
  bad: "bg-bad-soft text-bad",
  info: "bg-info-soft text-info",
  violet: "bg-violet-soft text-violet",
  outline: "border border-line text-ink-2",
  brand: "brand-bg text-white",
};

export function Badge({
  tone = "neutral",
  icon,
  size = "sm",
  mono,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: Tone; icon?: ReactNode; size?: "xs" | "sm" | "md"; mono?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md font-medium [&_svg]:shrink-0",
        size === "xs" && "h-5 px-1.5 text-[10.5px] [&_svg]:size-3",
        size === "sm" && "h-6 px-2 text-[11.5px] [&_svg]:size-3",
        size === "md" && "h-7 px-2.5 text-[12.5px] rounded-lg [&_svg]:size-3.5",
        mono && "num",
        TONES[tone],
        className,
      )}
      {...props}
    >
      {icon}
      {/* measurements read left-to-right even inside Arabic */}
      {mono ? <span dir="ltr">{children}</span> : <span className="truncate">{children}</span>}
    </span>
  );
}

/** A small status dot, optionally pulsing (live). */
export function Dot({ tone = "ok", pulse, className }: { tone?: "ok" | "warn" | "bad" | "accent" | "muted"; pulse?: boolean; className?: string }) {
  const color = { ok: "bg-ok", warn: "bg-warn", bad: "bg-bad", accent: "bg-accent", muted: "bg-ink-3" }[tone];
  return (
    <span className={cn("relative inline-flex size-2 shrink-0", className)} aria-hidden>
      {pulse && <span className={cn("absolute inset-0 rounded-full animate-pulse-ring", color)} />}
      <span className={cn("relative inline-flex size-2 rounded-full", color)} />
    </span>
  );
}

/** A count bubble for nav items and icons. */
export function CountBadge({ count, tone = "accent", className }: { count: number; tone?: "accent" | "bad" | "neutral"; className?: string }) {
  if (!count) return null;
  return (
    <span
      className={cn(
        "num inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold leading-none",
        tone === "accent" && "bg-accent-solid text-on-accent",
        tone === "bad" && "bg-bad-solid text-white",
        tone === "neutral" && "bg-surface-3 text-ink-2",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
